/* ══════════════════════════════════════════════════════════════════════
   ConsoleContainer — the live entry point mounted at /admin.
   Binds auth + live Firestore data + real actions to the pixel-faithful
   TrasseConsole view. This is the facelift: same UI, real data & actions.
   ══════════════════════════════════════════════════════════════════════ */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import TrasseConsole, { WizardPayload } from './TrasseConsole';
import { useConsoleData } from './useConsoleData';
import { AddonKey } from './consoleModel';
import { saveAddonCredentials, testAddonConnection, AddonKey as RealAddonKey } from '../services/addonsApi';
import { isPaymentsConfigured, sendAdminInviteEmailApi } from '../services/paymentsApi';
import { archiveOrganization, purgeOrganization, guardRemoval } from '../services/orgLifecycle';
import { adminInviteActionSettings } from '../components/selfServeSignup';
import { ADDON_PROVIDERS } from '../components/addonProviders';
import { applyManualEpraCeiling, resetRouteLearning, routeLearningStatus, rebuildRouteLearning, resetFuelData } from '../services/consoleApi';
import type { RouteLearningStatusTrip } from '../services/consoleApi';
import { healUnlearnableTrips } from './routeLearningHeal';

const REAL_ADDONS: AddonKey[] = ['gps', 'fuel', 'fees'];
const UNLIMITED = 999999;

/** Console UI lists provider display names; backend expects provider ids (e.g. karooooo). */
function resolveProviderId(addon: AddonKey, nameOrId: string): string {
  if (!nameOrId || !REAL_ADDONS.includes(addon)) return nameOrId;
  const list = ADDON_PROVIDERS[addon as RealAddonKey] || [];
  const byId = list.find((p) => p.id === nameOrId);
  if (byId) return byId.id;
  const byName = list.find((p) => p.name === nameOrId || p.shortName === nameOrId);
  return byName?.id || nameOrId;
}

/** Maps the wizard's kind/lorry selection to the stored productType. */
function productTypeFor(p: WizardPayload): string {
  if (p.kind === 'BUS_TRACKING') return 'BUS_ONLY';
  if (p.kind === 'LORRY') return p.lorryMode === 'CARGO' ? 'CARGO' : 'SELF_TRACKING';
  return 'FULL_FEATURE';
}

const ConsoleContainer: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const data = useConsoleData(currentUser as any);

  /* Provision a console-created school. Same org as the self-serve flow, with
     three differences: it's a BETA account that is NOT billed (subscriptionExempt
     — never expires, only a manual suspend disables it), it's premium-active
     immediately (no pilot), and the admin is invited by the SAME email-link that
     self-serve/fleet invites use (adminInviteActionSettings → /activate). */
  const onCreateSchool = async (p: WizardPayload) => {
    const code = p.fullCode;
    if (!code || code === 'CODE') throw new Error('Enter an organization code.');
    if (!p.email.trim()) throw new Error('Enter the admin email.');
    if (!(p.adminName || '').trim()) throw new Error("Enter the admin's name.");
    const isLorry = p.kind === 'LORRY';
    const cap = (n: number) => (!n || n <= 0 ? UNLIMITED : n);
    const email = p.email.trim().toLowerCase();
    // Unbilled + exempt → give a far-future access date so it never reads expired.
    const expiry = p.endDate ? new Date(p.endDate) : new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000);
    const orgDoc = {
      name: p.name.trim(),
      code,
      type: 'school' as const,
      productType: productTypeFor(p),
      address: p.addr || '',
      phone: p.phone || '',
      email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      studentCount: 0,
      teacherCount: 0,
      parentCount: 0,
      // premium, active immediately — no pilot
      subscriptionPlan: p.plan,
      subscriptionStatus: p.active ? 'active' : 'suspended',
      hasActiveSubscription: p.active,
      isActive: p.active,
      isPilot: false,
      expirationDate: expiry,
      billingPeriod: 'termly' as const,
      // usage caps so the beta org can't invite unlimited people
      maxStudents: cap(p.maxStudents),
      maxTeachers: cap(p.maxTeachers),
      maxParents: cap(p.maxParents),
      plannedChildren: cap(p.maxStudents),
      plannedParents: cap(p.maxParents),
      educationSystem: isLorry ? null : 'CBC',
      // ── BETA / not-billed markers ──
      beta: true,
      consoleProvisioned: true,
      billingType: 'manual',
      accountTag: 'beta',
      subscriptionExempt: true, // never auto-logs-out; only a manual suspend disables
      // console-provisioned (not the anonymous self-serve path); admin claims by email link
      selfServe: false,
      adminEmail: email,
      // The admin's own name: their account takes it when they accept the
      // invite, and the apps show it instead of the email (2026-10-27).
      adminName: p.adminName.trim(),
      adminEmails: [email],
      // Required for /activate claim + Firestore invite rules (same as Team → Invite admin).
      pendingAdminInvites: [email],
      pendingActivation: false,
      // geofencing anchor — required by the wizard; invariant: pending === !home
      homeLocations: p.homeLocations ?? null,
      locationSetupPending: !p.homeLocations,
    };
    await setDoc(doc(db, 'organizations', code), orgDoc, { merge: true });

    // Branded Brevo invite (same as fleet Team → Invite admin). Falls back to Firebase
    // client link only if payments backend isn't configured. Returns the raw Firebase
    // link so the console can copy it when Brevo click-trackers time out.
    const settings = adminInviteActionSettings(code);
    try {
      if (isPaymentsConfigured()) {
        const branded = await sendAdminInviteEmailApi({
          orgCode: code,
          email,
          continueUrl: settings.url,
        });
        if (!branded.ok) {
          throw new Error(branded.error || 'Could not send the admin invite.');
        }
        return { inviteLink: branded.link };
      }
      await sendSignInLinkToEmail(auth, email, settings);
    } catch (e: any) {
      throw new Error(`School created, but the invite email to ${email} failed (${e?.code || e?.message || 'unknown'}). You can re-send it from the school later.`);
    }
  };

  /* Backfill / edit an org's home location (the geofencing anchor). Writes the
     coordinate to organizations/{code} (merge) and clears the pending flag —
     upholding the invariant locationSetupPending === !homeLocations. */
  const onSaveHomeLocation = async (orgCode: string, loc: { lat: number; lng: number }) => {
    await setDoc(
      doc(db, 'organizations', orgCode),
      { homeLocations: { lat: loc.lat, lng: loc.lng }, locationSetupPending: false, updatedAt: serverTimestamp() },
      { merge: true },
    );
  };

  /* Save an add-on's credentials. gps/fuel/fees go server-side (never into the
     world-readable org doc); sms is a lightweight in-doc provider selection. */
  const onSaveConnection = async (orgCode: string, addon: AddonKey, creds: { provider: string; apiUrl: string; apiKey: string; secret: string }) => {
    const ref = doc(db, 'organizations', orgCode);
    if (REAL_ADDONS.includes(addon)) {
      const providerId = resolveProviderId(addon, creds.provider);
      const extraFields: Record<string, string> = creds.secret ? { password: creds.secret } : {};
      const saved = await saveAddonCredentials(orgCode, addon as RealAddonKey, { provider: providerId, apiUrl: creds.apiUrl, apiKey: creds.apiKey, extraFields });
      if (!saved.ok) return { ok: false, message: saved.message };
      try {
        await updateDoc(ref, {
          [`addons.${addon}.wanted`]: true,
          [`addons.${addon}.provider`]: creds.provider, // keep display name for UI card
          [`addons.${addon}.status`]: 'connected',
          [`addons.${addon}.verified`]: false,
          updatedAt: serverTimestamp(),
        });
      } catch { /* advisory */ }
      return { ok: true };
    }
    // sms — no verification backend; store provider selection only (no secret in doc)
    try {
      await updateDoc(ref, {
        [`addons.${addon}.wanted`]: true,
        [`addons.${addon}.provider`]: creds.provider,
        [`addons.${addon}.status`]: 'connected',
        [`addons.${addon}.verified`]: true,
        updatedAt: serverTimestamp(),
      });
      return { ok: true };
    } catch (e: any) { return { ok: false, message: e?.message || 'Could not save.' }; }
  };

  /* Live-verify an add-on. gps/fuel/fees hit the real backend; the org doc's
     verified flag is updated from the result. */
  const onTestConnection = async (orgCode: string, addon: AddonKey, _provider: string) => {
    if (!REAL_ADDONS.includes(addon)) return { ok: true, message: 'Provider set — SMS delivery is handled without a verification test.', sampleCount: 0 };
    const res = await testAddonConnection(orgCode, addon as RealAddonKey);
    try {
      await updateDoc(doc(db, 'organizations', orgCode), {
        [`addons.${addon}.status`]: res.ok ? 'connected' : 'default',
        [`addons.${addon}.verified`]: res.ok,
        [`addons.${addon}.verifiedAt`]: res.ok ? serverTimestamp() : null,
        [`addons.${addon}.lastTest`]: { ok: res.ok, message: res.message, sampleCount: res.sampleCount },
        updatedAt: serverTimestamp(),
      });
    } catch { /* advisory */ }
    return { ok: res.ok, message: res.message, sampleCount: res.sampleCount };
  };

  /* Archive: snapshot everything, then hide the org. Re-checks the billing
     guard here — the console button is only the first gate, and a stale page
     could still be showing an org whose plan went active since it loaded. */
  const onArchiveOrg = async (org: { code: string; name: string; status: string; plan?: string; hasActiveSubscription?: boolean | null }) => {
    const guard = guardRemoval(org.status, org.plan, { hasActiveSubscription: org.hasActiveSubscription });
    if (!guard.allowed) throw new Error(guard.reason);
    await archiveOrganization(org.code, auth.currentUser?.uid);
  };

  /* Permanent delete. The server deletes every record that names the org,
     logins and snapshots included, and keeps only its payment records
     (orgLifecycle.purgeOrganization). If the server does not delete, that
     throws and nothing is gone. A login that would not delete is reported,
     never fatal: the data is already gone by then and stopping helps nobody. */
  const onPurgeOrg = async (org: { code: string; name: string; status: string; plan?: string; hasActiveSubscription?: boolean | null }) => {
    const guard = guardRemoval(org.status, org.plan, { hasActiveSubscription: org.hasActiveSubscription });
    if (!guard.allowed) throw new Error(guard.reason);
    const result = await purgeOrganization(org.code);
    if (result.authFailed > 0) {
      throw new Error(
        `${org.name} was deleted, but ${result.authFailed} login ` +
        'account(s) could not be removed. They can still sign in to an empty app — clear them from ' +
        'the Firebase console.',
      );
    }
  };

  const onAcknowledgePlatformAlert = async (alertId: string) => {
    if (!alertId || !currentUser) return;
    try {
      await updateDoc(doc(db, 'platformAlerts', alertId), {
        acknowledged: true,
        acknowledgedAt: serverTimestamp(),
        acknowledgedBy: currentUser.uid,
      });
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.warn('[console] acknowledge platform alert failed', e?.message || e);
    }
  };

  const onApplyManualEpraCeiling = async (args: {
    alertId: string;
    diesel: number;
    petrol: number;
  }) => {
    const result = await applyManualEpraCeiling(args);
    if (!result.ok) {
      throw new Error(result.error || result.reason || 'Could not apply ceilings.');
    }
    return result;
  };

  /* Forget an org's route learning. Server-side: it deletes documents the
     rules do not let a browser touch, and stamps the org so the nightly
     reconcile does not rebuild the same clusters from the same old trips. */
  const onResetRouteLearning = async (org: { code: string; name: string }) => {
    const result = await resetRouteLearning(org.code);
    if (!result.ok) throw new Error(result.error || 'Could not reset route learning.');
    return { deleted: result.deleted ?? 0 };
  };

  /* Why hasn't a route learned? Read-only readout of what the learner sees
     (bug 129) — the trips since the reset and the clusters they form. */
  const onRouteLearningStatus = async (org: { code: string; name: string }) => {
    const result = await routeLearningStatus(org.code);
    if (!result.ok) throw new Error(result.error || 'Could not read route learning.');
    return result;
  };

  /* Heal trips the sealed-trail bug left unlearnable (135), then rebuild this
     org's routes now instead of at 06:00. Runs in the browser: the dashboard
     holds the location key and the platform admin may update any trip. */
  const onRouteLearningHeal = async (org: { code: string; name: string }, trips: RouteLearningStatusTrip[]) => {
    const h = await healUnlearnableTrips(trips);
    const r = await rebuildRouteLearning(org.code);
    const s = (r.summary || {}) as Record<string, unknown>;
    // reconcile.js summary: { profilesRebuilt, tripsFolded, tripsSkipped, … }
    const rebuilt = typeof s.profilesRebuilt === 'number' ? s.profilesRebuilt : null;
    /* Snap truth, on screen. Three blind Heals in a row looked identical while
       the map stayed raw (Gabriel, 2026-08-24: "what exactly are you doing??")
       — because the WHY only ever lived on the trip docs and in server logs. */
    const snapOn = r.ok ? s.roadSnapEnabled === true : null;
    const snapped = typeof s.tripsRoadSnapped === 'number' ? s.tripsRoadSnapped : 0;
    const snapFailed = typeof s.tripsSnapFailed === 'number' ? s.tripsSnapFailed : 0;
    const snapError = typeof s.snapErrorSample === 'string' && s.snapErrorSample ? s.snapErrorSample : null;
    /* Road naming now happens inside the rebuild itself (the 15-min sweep is a
       separate, rarely-redeployed cron service) — surface what naming did. */
    const roads = (s.roads || {}) as Record<string, unknown>;
    const roadsNamed = typeof roads.roadsNamed === 'number' ? roads.roadsNamed : 0;
    const nameErrors = typeof roads.nameErrors === 'number' ? roads.nameErrors : 0;
    const nameError = typeof roads.nameErrorSample === 'string' && roads.nameErrorSample
      ? roads.nameErrorSample
      : (typeof roads.error === 'string' ? roads.error : null);
    return { healed: h.healed, skipped: h.skipped, rebuilt, snapOn, snapped, snapFailed, snapError, roadsNamed, nameErrors, nameError, error: r.ok ? h.errors[0] : (r.error || 'Rebuild failed.') };
  };

  /* Wipe an org's fuel data so testing starts clean (Gabriel, 2026-08-23).
     Server-side: deletes across six collections + bus-doc fields the rules
     do not let a browser touch. Config, prices and trips are kept. */
  const onResetFuelData = async (org: { code: string; name: string }) => {
    const result = await resetFuelData(org.code);
    if (!result.ok) throw new Error(result.error || 'Could not reset fuel data.');
    return { deleted: result.deleted ?? 0, counts: result.counts };
  };

  return (
    <TrasseConsole
      data={data}
      actions={{
        onLogout: () => { logout().then(() => navigate('/sign-in')).catch(() => navigate('/sign-in')); },
        onCreateSchool,
        onContactEmail: (a) => { if (a.email) window.open(`mailto:${a.email}`); },
        onContactCall: (a) => { if (a.phone) window.open(`tel:${a.phone}`); },
        onSaveConnection,
        onTestConnection,
        onSaveHomeLocation,
        onArchiveOrg,
        onPurgeOrg,
        onAcknowledgePlatformAlert,
        onApplyManualEpraCeiling,
        onResetRouteLearning,
        onRouteLearningStatus,
        onRouteLearningHeal,
        onResetFuelData,
      }}
    />
  );
};

export default ConsoleContainer;
